import React, { useMemo } from 'react';
import MarkdownIt from 'markdown-it';
import DOMPurify from 'dompurify';

import { usePanelPlatform } from '../panel/PanelData';

const HIGHLIGHT_TAGS = ['risk', 'opportunity', 'caution', 'info'] as const;

/**
 * markdown-it plugin: rewrites the backend's inline highlight tags
 * (`[risk]…[/risk]`, `[opportunity]…[/opportunity]`, `[caution]…[/caution]`,
 * `[info]…[/info]`) into `<span class="hl-risk">…</span>` etc. so the body
 * Box's sx can colour them. The tags survive markdown parsing as plain text
 * (a bare `[risk]` is not a link), so we rewrite at the token level after
 * inline parsing — this also lets a highlighted phrase contain other inline
 * markup (e.g. `[risk]**bad**[/risk]`).
 */
function highlightPlugin(md: MarkdownIt): void {
  md.core.ruler.push('dooglie_highlight', (state) => {
    for (const block of state.tokens) {
      if (block.type !== 'inline' || !block.children) continue;

      const out: typeof block.children = [];
      const openStack: string[] = [];

      for (const tok of block.children) {
        if (tok.type !== 'text' || !/[[<]/.test(tok.content)) {
          out.push(tok);
          continue;
        }

        // Canonical form is square brackets ([risk]…[/risk]), but the model
        // sometimes drifts to the angle-bracket form (<risk>…</risk>) — which
        // markdown-it (html:false) would otherwise escape to inert plain text.
        // Accept either bracket style so both render as coloured highlights.
        const re = /[[<](\/?)(risk|opportunity|caution|info)[\]>]/g;
        const content = tok.content;
        let last = 0;
        let m: RegExpExecArray | null;

        const pushText = (text: string) => {
          if (!text) return;
          const t = new state.Token('text', '', 0);
          t.content = text;
          out.push(t);
        };

        while ((m = re.exec(content)) !== null) {
          pushText(content.slice(last, m.index));
          const isClose = m[1] === '/';
          const name = m[2];

          if (!isClose) {
            const open = new state.Token('span_open', 'span', 1);
            open.attrSet('class', `hl-${name}`);
            out.push(open);
            openStack.push(name);
          } else if (openStack[openStack.length - 1] === name) {
            openStack.pop();
            out.push(new state.Token('span_close', 'span', -1));
          } else {
            // Unmatched close tag — keep it as literal text.
            pushText(m[0]);
          }
          last = m.index + m[0].length;
        }
        pushText(content.slice(last));
      }

      // Defensively close any tag the LLM left open.
      while (openStack.pop()) {
        out.push(new state.Token('span_close', 'span', -1));
      }

      block.children = out;
    }
    return true;
  });
}

const md = new MarkdownIt({
  html: false,
  linkify: true,
  breaks: true,
  typographer: true,
}).use(highlightPlugin);

// A fenced block (the Desk's drafted message) is drawn with a Copy button above
// it. The button is plain markup: `Markdown`'s click handler does the copying.
const COPY_BUTTON = '<button type="button" class="md-copy">Copy</button>';
md.renderer.rules.fence = (tokens, idx) => {
  const code = md.utils.escapeHtml(tokens[idx].content);
  return `<div class="md-code">${COPY_BUTTON}<pre><code>${code}</code></pre></div>\n`;
};

/**
 * The summary variant: same markdown, but list items render as block-display
 * spans instead of `<ul><li>`.
 *
 * A card summary lives inside `AccordionSummary`, whose MUI root is `ButtonBase`
 * — a real `<button>`. `<ul>`/`<li>` are flow content and are invalid inside a
 * button, and browsers may reparent them. Spans are phrasing content, so they
 * nest legally, and they keep the summary's `-webkit-line-clamp` working, which
 * only clamps inline content.
 *
 * `linkify` is off here: an autolinked URL inside the accordion header would
 * open a tab *and* toggle the accordion on the same click.
 */
const summaryMd = new MarkdownIt({
  html: false,
  linkify: false,
  breaks: true,
  typographer: true,
}).use(highlightPlugin);

summaryMd.renderer.rules.bullet_list_open = () => '';
summaryMd.renderer.rules.bullet_list_close = () => '';
summaryMd.renderer.rules.ordered_list_open = () => '';
summaryMd.renderer.rules.ordered_list_close = () => '';
summaryMd.renderer.rules.list_item_open = () => '<span class="md-li">';
summaryMd.renderer.rules.list_item_close = () => '</span>';
// A single bullet would otherwise be wrapped in <p> by the block renderer and
// stack an empty line above it.
summaryMd.renderer.rules.paragraph_open = () => '';
summaryMd.renderer.rules.paragraph_close = () => '';

/**
 * A line carrying two list markers (`- - the point`, `- • the point`), which
 * markdown reads as a one-item list nested inside another.
 *
 * The backend builds a card's bullets by prefixing `- ` to each item its brain
 * returned, so a brain that writes its own marker into the item text ships two.
 * Nested-list markup is then correct-but-absurd: an outer marker wrapping a
 * single inner one, which the body renders as `• ◦ text` and the summary as a
 * bare bullet with the text indented beneath it.
 *
 * Only same-line doubling is matched, so a genuinely nested list — which is
 * indented onto its own line — renders as written. `- -5% margin` is left alone
 * too: a marker has to be followed by whitespace.
 */
const DOUBLED_MARKER = /^([ \t]*)([-*+])[ \t]+(?:[-*+•][ \t]+)+/gm;

const collapseDoubledMarkers = (source: string): string =>
  source.replace(DOUBLED_MARKER, '$1$2 ');

// The highlight plugin is the only source of <span class>; restrict DOMPurify
// to the known `hl-*` classes (plus the summary variant's list-item marker) so
// nothing else can sneak a class attribute through.
const ALLOWED_HL_CLASSES = new Set([
  ...HIGHLIGHT_TAGS.map((t) => `hl-${t}`),
  'md-li',
  'md-code',
  'md-copy',
]);

interface MarkdownProps {
  source: string | null | undefined;
  className?: string;
  style?: React.CSSProperties;
  /**
   * `block` (default) is the full renderer for card bodies. `summary` is the
   * inline-only renderer described above, for the always-visible card line.
   */
  variant?: 'block' | 'summary';
}

/**
 * Lightweight read-only markdown renderer. markdown-it + DOMPurify, no
 * Tiptap editor instance — Tiptap is reserved for editable surfaces where
 * the editing toolbar is actually used.
 */
/**
 * Render + sanitise, exported so the two variants' output can be asserted
 * directly rather than through a mounted component.
 */
export function renderMarkdown(
  source: string | null | undefined,
  variant: 'block' | 'summary' = 'block'
): string {
  const rendered = (variant === 'summary' ? summaryMd : md).render(
    collapseDoubledMarkers(source || '')
  );
  return DOMPurify.sanitize(rendered, {
    ADD_ATTR: ['class'],
    ALLOWED_ATTR: ['href', 'title', 'target', 'rel', 'class', 'type'],
  }).replace(/class="([^"]*)"/g, (full, cls: string) =>
    ALLOWED_HL_CLASSES.has(cls) ? full : ''
  );
}

/** Copy the code block beside a Copy button, and say so on the button for a moment. */
const copyBlock = async (button: HTMLButtonElement): Promise<void> => {
  const text = button.parentElement?.querySelector('pre')?.textContent ?? '';
  let label = 'Copied';
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    label = "Couldn't copy";
  }
  button.textContent = label;
  window.setTimeout(() => {
    button.textContent = 'Copy';
  }, 1500);
};

const Markdown: React.FC<MarkdownProps> = ({
  source,
  className,
  style,
  variant = 'block',
}) => {
  const html = useMemo(() => renderMarkdown(source, variant), [source, variant]);
  const { openLink } = usePanelPlatform();

  const onClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const copy = (event.target as HTMLElement).closest('button.md-copy');
    if (copy) {
      event.preventDefault();
      event.stopPropagation();
      void copyBlock(copy as HTMLButtonElement);
      return;
    }
    const anchor = (event.target as HTMLElement).closest('a');
    if (!anchor?.href || anchor.href.startsWith('javascript:')) return;
    event.preventDefault();
    openLink(anchor.href);
  };

  return (
    <div
      className={className}
      style={style}
      onClick={onClick}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

export default Markdown;
